import { defaultSEO, courseDefaults } from "@/config/seoConfig";

// ─── Interfaces ─────────────────────────────────────────────────────────────

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface CourseSchemaInput {
  title: string;
  description?: string;
  metaDescription?: string;
  slug?: string;
  price?: string | number;
  level?: string;
  whatYoullLearn?: string[];
  aggregateRating?: {
    ratingValue: number | string;
    reviewCount: number | string;
    bestRating?: number | string;
    worstRating?: number | string;
  };
}

export interface BlogPostSchemaInput {
  title?: string;
  description?: string;
  excerpt?: string;
  slug?: string;
  url?: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  date?: string;
}

export interface TestimonialItem {
  author?: string;
  name?: string;
  rating?: number;
  text?: string;
  content?: string;
  course?: string;
}

// ─── 1. Organization ────────────────────────────────────────────────────────

export function generateOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["Organization", "EducationalOrganization"],
    name: defaultSEO.siteName,
    url: defaultSEO.siteUrl,
    logo: {
      "@type": "ImageObject",
      url: `${defaultSEO.siteUrl}/images/og-default.jpg`,
    },
    description: defaultSEO.defaultDescription,
    address: {
      "@type": "PostalAddress",
      streetAddress: defaultSEO.address.street,
      addressLocality: defaultSEO.address.city,
      addressRegion: defaultSEO.address.state,
      postalCode: defaultSEO.address.zip,
      addressCountry: defaultSEO.address.country,
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: defaultSEO.phone,
      email: defaultSEO.email,
      contactType: "customer service",
      availableLanguage: "English",
    },
    sameAs: Object.values(defaultSEO.social),
  };
}

// ─── 2. Website ─────────────────────────────────────────────────────────────

export function generateWebsiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: defaultSEO.siteName,
    url: defaultSEO.siteUrl,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${defaultSEO.siteUrl}/courses?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

// ─── 3. Course ───────────────────────────────────────────────────────────────

export function generateCourseSchema(course: CourseSchemaInput) {
  const courseUrl = `${defaultSEO.siteUrl}/courses/${course.slug?.replace("/courses/", "") ?? ""}`;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: course.title,
    description: course.description ?? course.metaDescription ?? defaultSEO.defaultDescription,
    url: courseUrl,
    provider: {
      "@type": "Organization",
      name: courseDefaults.provider,
      sameAs: courseDefaults.providerUrl,
    },
    offers: {
      "@type": "Offer",
      price: course.price ?? "0",
      priceCurrency: courseDefaults.currency,
      availability: "https://schema.org/InStock",
      url: courseUrl,
    },
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: courseDefaults.mode,
      inLanguage: courseDefaults.language,
    },
  };

  if (course.level) {
    schema.educationalLevel = course.level;
  }

  if (Array.isArray(course.whatYoullLearn) && course.whatYoullLearn.length > 0) {
    schema.teaches = course.whatYoullLearn;
  }

  if (course.aggregateRating) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: course.aggregateRating.ratingValue,
      reviewCount: course.aggregateRating.reviewCount,
      bestRating: course.aggregateRating.bestRating ?? 5,
      worstRating: course.aggregateRating.worstRating ?? 1,
    };
  }

  return schema;
}

// ─── 4. Breadcrumb ───────────────────────────────────────────────────────────

export function generateBreadcrumbSchema(breadcrumbs: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url.startsWith("http")
        ? crumb.url
        : `${defaultSEO.siteUrl}${crumb.url}`,
    })),
  };
}

// ─── 5. Blog Post ────────────────────────────────────────────────────────────

export function generateBlogPostSchema(post: BlogPostSchemaInput) {
  const postUrl = post.url
    ? post.url
    : `${defaultSEO.siteUrl}/blog/${post.slug ?? ""}`;

  const imageUrl = post.image
    ? post.image.startsWith("http")
      ? post.image
      : `${defaultSEO.siteUrl}${post.image}`
    : `${defaultSEO.siteUrl}${defaultSEO.defaultOgImage}`;

  return {
    "@context": "https://schema.org",
    "@type": ["BlogPosting", "Article"],
    headline: post.title,
    description: post.excerpt ?? post.description ?? defaultSEO.defaultDescription,
    url: postUrl,
    datePublished: post.datePublished ?? post.date ?? new Date().toISOString(),
    dateModified: post.dateModified ?? post.datePublished ?? post.date ?? new Date().toISOString(),
    author: {
      "@type": "Organization",
      name: defaultSEO.siteName,
      url: defaultSEO.siteUrl,
    },
    publisher: {
      "@type": "Organization",
      name: defaultSEO.siteName,
      logo: {
        "@type": "ImageObject",
        url: `${defaultSEO.siteUrl}/images/og-default.jpg`,
      },
    },
    image: {
      "@type": "ImageObject",
      url: imageUrl,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": postUrl,
    },
  };
}

// ─── 6. FAQ ──────────────────────────────────────────────────────────────────

export function generateFAQSchema(faqs: FAQItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

// ─── 7. Local Business ───────────────────────────────────────────────────────

export function generateLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "EducationalOrganization"],
    name: defaultSEO.siteName,
    image: `${defaultSEO.siteUrl}/images/og-default.jpg`,
    url: defaultSEO.siteUrl,
    telephone: defaultSEO.phone,
    email: defaultSEO.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: defaultSEO.address.street,
      addressLocality: defaultSEO.address.city,
      addressRegion: defaultSEO.address.state,
      postalCode: defaultSEO.address.zip,
      addressCountry: defaultSEO.address.country,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.7508,
      longitude: -88.1535,
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "09:00",
        closes: "19:00",
      },
    ],
    priceRange: "$$",
    hasMap: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${defaultSEO.address.street}, ${defaultSEO.address.city}, ${defaultSEO.address.state} ${defaultSEO.address.zip}`
    )}`,
  };
}

// ─── 8. Reviews / Aggregate Rating ──────────────────────────────────────────

export function generateReviewSchema(testimonials: TestimonialItem[]) {
  const totalRating = testimonials.reduce((sum, t) => sum + (t.rating ?? 5), 0);
  const avgRating = testimonials.length > 0 ? (totalRating / testimonials.length).toFixed(1) : "5.0";

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: defaultSEO.siteName,
    url: defaultSEO.siteUrl,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: avgRating,
      reviewCount: testimonials.length,
      bestRating: 5,
      worstRating: 1,
    },
    review: testimonials.map((t) => ({
      "@type": "Review",
      author: {
        "@type": "Person",
        name: t.author || t.name || "Anonymous",
      },
      reviewRating: {
        "@type": "Rating",
        ratingValue: t.rating ?? 5,
        bestRating: 5,
        worstRating: 1,
      },
      reviewBody: t.text || t.content || "",
      ...(t.course && {
        itemReviewed: {
          "@type": "Course",
          name: t.course,
        },
      }),
    })),
  };
}
